/**
 * AnchorNav — вертикальная рейка разделов, привязанная к прокрутке.
 *
 * Это оглавление, а не вкладки: все разделы остаются на странице, рейка
 * лишь перемещает к ним и показывает, где читатель сейчас. Вертикаль здесь
 * не украшение — оглавление документа читается сверху вниз, тем же
 * движением, что и сам документ, и умещает одиннадцать подписей без
 * прокрутки вбок и без сокращений.
 *
 * Связь с прокруткой двойная. Активный пункт подсвечивается наблюдателем
 * пересечений (`useActiveSection`), а слева по рейке идёт линия прогресса.
 * Обе величины считаются от одной отметки на экране, поэтому кончик линии
 * всегда стоит на активном пункте: подсветка отвечает «где я», линия —
 * «сколько осталось», и спорить между собой им нечем.
 *
 * Навигация нативная: каждая ячейка — настоящий `<a href="#section">`, что
 * даёт хеш в адресе, работающую кнопку «назад» и открытие в новой вкладке.
 * Обработчик клика ничего из этого не даёт. Смещение приземления делает CSS
 * (`scroll-margin-top` на разделе), а не JavaScript.
 *
 * `SegmentedControl` здесь больше не используется: он описывает
 * горизонтальный набор взаимоисключающих ячеек, а оглавление — список
 * ссылок. Прежняя сборка требовала переворачивать его флекс снаружи, то
 * есть лезть в чужие внутренности.
 *
 * Шаг между пунктами ровный. Паузы между стадиями пути сбивали ритм —
 * подписи шли то через одно расстояние, то через другое, и оглавление
 * читалось как несколько списков подряд. Стадии никуда не делись, но
 * показывает их теперь линия прогресса: шкала у неё ровная, а скорость
 * разная, потому что разделы разной длины.
 *
 * На узком экране рейка ложится обратно в горизонтальную ленту: колонка
 * шириной в треть экрана съела бы место у самого документа.
 *
 * @format
 */

import {useEffect, useMemo, useState} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Stack} from 'ui/components/layout/Stack';
import {spacingPx} from 'ui/components/layout/spacing';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';
import {MotionDurationBase, MotionEasingStandard} from 'ui/tokens/ts/motion';

import type {NavAnchor, SectionId} from '../types';

export interface AnchorNavProps {
  anchors: NavAnchor[];
  /** Currently visible section, if the page tracks it. */
  activeId?: SectionId | null;
}

/**
 * Ширина колонки рейки и порог, ниже которого она ложится горизонтально.
 *
 * 184 — по самой длинной подписи («Деньги под риском») плюс отступ линии.
 * Шире колонка забирала бы у документа место, ничего не добавляя: пункты
 * оглавления короткие и вторую строку не занимают.
 */
export const NAV_WIDTH = 184;
const NARROW = 1024;

/** Высота шапки приложения — от неё липнет рейка. */
const HEADER_OFFSET = 72;

const Rail = styled(Box)({
  position: 'sticky',
  top: HEADER_OFFSET,
  alignSelf: 'flex-start',
  width: NAV_WIDTH,
  flex: 'none',

  [`@media (max-width: ${NARROW}px)`]: {
    // Горизонтальная лента: у неё своя липкость (к самому верху) и своя
    // прокрутка — вертикальная колонка на узком экране забрала бы у
    // документа треть ширины.
    position: 'sticky',
    top: 0,
    zIndex: 20,
    // Во всю ширину колонки, а не по содержимому. Базовый `alignSelf:
    // flex-start` нужен вертикальной рейке, чтобы липкая колонка не
    // растягивалась на высоту документа. Но ниже порога ряд становится
    // колонкой, и та же строчка начинает задавать ШИРИНУ по содержимому:
    // лента из двенадцати пунктов раздувалась до 1234px, обрезать было
    // нечего, и вбок уезжала вся страница.
    alignSelf: 'stretch',
    width: 'auto',
    maxWidth: '100%',
    minWidth: 0,
    paddingTop: spacingPx('s'),
    paddingBottom: spacingPx('xs'),
    backgroundColor: 'var(--ads-palette-background-default)',
    borderBottom: `1px solid ${surfaceVar('surface1')}`,
    overflowX: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: `${surfaceVar('surface3')} transparent`,
    '&::-webkit-scrollbar': {height: 6},
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: surfaceVar('surface3'),
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-track': {backgroundColor: 'transparent'},
  },
});

/**
 * Список пунктов и линия прогресса слева от него.
 *
 * Линия одна на всю рейку, а не по кусочку на пункт: прогресс — величина
 * непрерывная, и собранная из отрезков она читалась бы как шкала разделов,
 * которых прочитано целое число.
 */
const List = styled(Stack)({
  position: 'relative',
  paddingLeft: spacingPx('m'),

  [`@media (max-width: ${NARROW}px)`]: {
    flexDirection: 'row',
    alignItems: 'center',
    width: 'max-content',
    paddingLeft: 0,
  },
});

const Rule = styled(Box)({
  position: 'absolute',
  left: 0,
  top: 0,
  bottom: 0,
  width: 2,
  borderRadius: 1,
  backgroundColor: surfaceVar('surface2'),

  // Горизонтально линия прогресса не рисуется: она подпирала бы ленту
  // снизу и спорила с её собственной границей.
  [`@media (max-width: ${NARROW}px)`]: {display: 'none'},
});

/** Пройденная часть документа. Высота приходит инлайном — она меняется на скролле. */
const RuleFill = styled(Box)(({theme}) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  width: 2,
  borderRadius: 1,
  backgroundColor: theme.palette.primary.main,
  transition: `height ${MotionDurationBase} ${MotionEasingStandard}`,

  [`@media (max-width: ${NARROW}px)`]: {display: 'none'},
}));

/**
 * Пункт оглавления.
 *
 * Активный отличается цветом текста, а не заливкой: заливка сделала бы из
 * строки кнопку, а это ссылка в документе. Слева у активного — метка на
 * линии прогресса, она же связывает подсветку с самой линией.
 */
const Item = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $active: boolean;
  // `styled(Box)` типизирует пропы по вызову для div и теряет полиморфную
  // перегрузку: с `as='a'` атрибуты ссылки иначе не проходят проверку.
  // Объявляем их здесь — тот же приём, что и у `href` в `ChipMUI`.
  href?: string;
  target?: string;
}>(({theme, $active}) => ({
  ...text('body.m'),
  position: 'relative',
  display: 'block',
  color: $active ? surfaceVar('onSurface') : surfaceVar('onSurfaceMuted'),
  textDecoration: 'none',
  paddingTop: spacingPx('xs'),
  paddingBottom: spacingPx('xs'),
  transition: `color ${MotionDurationBase} ${MotionEasingStandard}`,

  '&:hover': {color: surfaceVar('onSurface')},

  // Метка активного пункта на линии: короткий отрезок ярче самой линии.
  ...($active
    ? {
        '&::before': {
          content: '""',
          position: 'absolute',
          left: -spacingPx('m'),
          top: spacingPx('xs'),
          bottom: spacingPx('xs'),
          width: 2,
          borderRadius: 1,
          backgroundColor: theme.palette.primary.main,
        },
      }
    : null),

  [`@media (max-width: ${NARROW}px)`]: {
    whiteSpace: 'nowrap',
    flex: 'none',
    paddingLeft: spacingPx('s'),
    paddingRight: spacingPx('s'),
    ...($active ? {'&::before': {display: 'none'}} : null),
  },
}));

/**
 * Где на экране проходит «строка чтения».
 *
 * Пятая часть высоты окна сверху — та же отметка, от которой считает
 * активный раздел (`rootMargin: '-20% …'`). Иначе подсветка и линия
 * говорили бы о разных местах документа.
 */
const READ_LINE = 0.2;

/**
 * Прогресс в координатах списка, 0..1.
 *
 * Не доля прокрутки страницы: пункты стоят ровным шагом, а разделы — нет,
 * и на общей шкале линия неизбежно расходилась с подсветкой. Здесь
 * величина считается как «пройдено разделов плюс доля текущего», поэтому
 * кончик линии всегда приходится на активный пункт, а скорость внутри
 * раздела зависит от его длины.
 *
 * Половина шага добавляется, чтобы кончик стоял по центру пункта, а не на
 * его верхней кромке: подпись читается серединой строки.
 */
function useSectionProgress(ids: SectionId[]): number {
  const [progress, setProgress] = useState(ids.length > 0 ? 0.5 / ids.length : 0);

  useEffect(() => {
    if (ids.length === 0) return undefined;

    const measure = () => {
      const doc = document.documentElement;
      // Абсолютные позиции разделов: относительные меняются на каждом
      // пикселе прокрутки, и сравнивать их с ней самой нельзя.
      const tops = ids.map(id => {
        const el = document.getElementById(id);
        return el ? el.getBoundingClientRect().top + window.scrollY : Number.POSITIVE_INFINITY;
      });

      const line = window.scrollY + window.innerHeight * READ_LINE;

      let index = 0;
      while (index + 1 < tops.length && tops[index + 1] <= line) index += 1;

      const from = tops[index];
      // У последнего раздела нет следующего — его границей служит конец
      // документа, иначе последняя доля всегда выходила бы нулевой.
      const to = index + 1 < tops.length ? tops[index + 1] : doc.scrollHeight;
      const span = Math.max(1, to - from);
      const within = Math.min(1, Math.max(0, (line - from) / span));

      setProgress((index + within + 0.5) / ids.length);
    };

    // Без кадровой очереди: событие прокрутки браузер и так выдаёт раз в
    // кадр, а очередь добавляла отказ — в невидимой вкладке кадров нет, и
    // линия замирала бы до возвращения на вкладку.
    measure();
    window.addEventListener('scroll', measure, {passive: true});
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [ids]);

  return progress;
}

export function AnchorNav({anchors, activeId}: AnchorNavProps) {
  // Мемо по ссылке на `anchors`: без него массив новый на каждый рендер, и
  // эффект переподписывался бы бесконечно.
  const ids = useMemo(() => anchors.map(anchor => anchor.id), [anchors]);
  const progress = useSectionProgress(ids);

  return (
    <Rail as='nav' aria-label='Разделы отчёта'>
      <List gap='none'>
        <Rule aria-hidden />
        <RuleFill aria-hidden style={{height: `${progress * 100}%`}} />

        {anchors.map(anchor => {
          const active = activeId === anchor.id;
          return (
            <Item
              key={anchor.id}
              as='a'
              href={`#${anchor.id}`}
              // Явно: превью Storybook несёт <base target="_parent">, иначе
              // ссылка утащила бы страницу из фрейма.
              target='_self'
              $active={active}
              aria-current={active ? 'location' : undefined}
            >
              {anchor.label}
            </Item>
          );
        })}
      </List>
    </Rail>
  );
}

/**
 * Track which section is in view. Kept next to the nav because it exists
 * only to feed it; the page just forwards the result back down.
 */
export function useActiveSection(ids: SectionId[]): SectionId | null {
  const [active, setActive] = useState<SectionId | null>(ids[0] ?? null);

  useEffect(() => {
    // Intersection state has to be accumulated across callbacks. A batch
    // reports only the sections whose state CHANGED, so deciding from the
    // batch alone leaves the highlight stale on any scroll where nothing
    // newly enters the detection band — which is most of them once the
    // sections are taller than the band itself.
    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        // Nothing in the band (between two long sections): keep the last
        // known anchor rather than clearing the rail.
        if (visible.size === 0) return;
        // Measure live — positions recorded at callback time go stale as
        // soon as the scroll continues.
        const ranked = [...visible]
          .map(id => ({id, top: document.getElementById(id)?.getBoundingClientRect().top ?? 0}))
          .sort((a, b) => a.top - b.top);
        if (ranked.length > 0) setActive(ranked[0].id as SectionId);
      },
      {rootMargin: '-20% 0px -70% 0px'},
    );

    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [ids]);

  return active;
}
